/**
 * Content module for surface 3.1 — PROJECT OVERVIEW.
 *
 * PX-W7.6 (TASK-FD4686D1) retired the KPI row, the needs-a-human table and
 * the team roster onto live slices (`features/requirements`,
 * `features/board`, the already-live `features/budgets` and the project
 * members projection) — none of them import from here any more. This module
 * SURVIVES, pruned to what the one remaining badged part of the page — the
 * overnight-runs panel — still needs: `AGENT_PANEL_TITLE`, `OVERNIGHT_RUNS`,
 * `RUN_LOG_FOOTNOTE`, `RUN_LOG_LINK` and the routes those runs link to.
 * Every symbol only the retired parts used (`KPI`, `BUDGET_FIGURE`,
 * `HUMAN_TODOS`, `TEAM_IDS`, `dueLabel`, …) has been deleted, per the epic's
 * own AC-5: this file is not retired, but nothing orphaned survives in it.
 *
 * The split-accepted chip (`SPLIT_CHIP_LABEL`, read from
 * `selectSplitAccepted` on the demo slice) was removed unconditionally by
 * the same ticket — `OvernightRun.reflectsSplit` no longer has a reader —
 * see the code-changes artefact for the three grounds.
 */
import { heroProject } from '@/demo/data'

const hero = heroProject()
const projectBase = `/project/${hero.id}`

/** Real routes — the overnight-runs panel never links to a placeholder. */
export const OVERVIEW_ROUTES = {
  build: `${projectBase}/build`,
  blueprint: `${projectBase}/blueprint`,
  migration: `${projectBase}/migration`,
  test: `${projectBase}/test`,
} as const

/* ── Panel: overnight agent runs ────────────────────────────────────────── */

export type RunTone = 'done' | 'review' | 'blocked'

export interface OvernightRun {
  id: string
  /** Which agent in the library produced it — provenance, not decoration. */
  agentId: string
  tone: RunTone
  label: string
  text: string
  time: string
  /** The surface that owns the run, i.e. where its log lives. */
  runHref: string
}

export const AGENT_PANEL_TITLE = 'OVERNIGHT · WHAT THE AGENTS DID'

export const OVERNIGHT_RUNS: readonly OvernightRun[] = [
  {
    id: 'run-scaffold',
    agentId: 'module-scaffolder',
    tone: 'done',
    label: 'Done',
    text: '4 modules scaffolded — pricing, promo, store hierarchy, loyalty',
    time: '02:10',
    runHref: OVERVIEW_ROUTES.build,
  },
  {
    id: 'run-regression',
    agentId: 'test-author',
    tone: 'done',
    label: 'Done',
    text: '61 regression cases generated from requirements #31–#78',
    time: '03:45',
    runHref: OVERVIEW_ROUTES.test,
  },
  {
    id: 'run-mapping',
    agentId: 'mapping-generator',
    tone: 'review',
    label: 'Review',
    text: 'Field mapping for 9 legacy price lists — 3 ambiguous columns',
    time: '04:20',
    runHref: OVERVIEW_ROUTES.migration,
  },
  {
    id: 'run-loyalty',
    agentId: 'mapping-generator',
    tone: 'blocked',
    label: 'Blocked',
    text: 'Loyalty import — source export missing 2 columns',
    time: '05:02',
    runHref: OVERVIEW_ROUTES.migration,
  },
] as const

export const RUN_LOG_FOOTNOTE = 'Every run is logged with inputs, outputs and the human who accepts it.'
export const RUN_LOG_LINK = { label: 'view the run log', to: OVERVIEW_ROUTES.build } as const
